/**
 * Build a hierarchical menu tree from a flat list of modules.
 * @param {Array} modules - List of module objects
 * @returns {Array} - Nested menu items
 */
export const buildMenuTree = (modules) => {
  if (!Array.isArray(modules) || modules.length === 0) {
    return [];
  }

  // Filter only modules allowed in menu (handles boolean, string, or numeric truthiness)
  const isTruthy = (val) => val === true || val === "true" || val === 1 || val === "1";
  
  const menuModules = modules.filter(m => isTruthy(m.allowInMenu));

  const map = {};
  const roots = [];

  // Initialize map with allowed modules
  menuModules.forEach(module => {
    map[module._id] = { ...module, children: [] };
  });

  // Build tree
  menuModules.forEach(module => {
    const parentId = module.parentModuleId;
    if (parentId && map[parentId]) {
      // Add as child only if parent is also allowed in menu
      map[parentId].children.push(map[module._id]);
    } else if (!parentId) {
      // Add as root only if it has no parent
      roots.push(map[module._id]);
    }
    // Note: Modules with parentId that are NOT in map (parent hidden) 
    // are intentionally excluded from 'roots' to maintain hierarchy integrity.
  });

  // Sort by displayOrder
  const sortByDisplayOrder = (items) => {
    items.sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0));
    items.forEach(item => {
      if (item.children.length > 0) {
        sortByDisplayOrder(item.children);
      }
    });
  };

  sortByDisplayOrder(roots);
  return roots;
};

/**
 * Get all authorized URLs as a flat set for quick lookup
 * @param {Array} modules 
 * @returns {Set}
 */
export const getAuthorizedUrls = (modules) => {
  if (!Array.isArray(modules)) return new Set();
  const urls = new Set();
  modules.forEach(m => {
    if (m.moduleUrl) {
      const url = m.moduleUrl.toLowerCase();
      if (urls.has(url)) {
        console.warn(`Duplicate module URL detected: ${url}. Module: ${m.moduleName}`);
      }
      urls.add(url);
    }
  });
  return urls;
};

export const getTabsByParent = (modules, parentIdentifier) => {
  if (!Array.isArray(modules) || !parentIdentifier) return [];

  let parent;
  
  // If parentIdentifier looks like an ID (doesn't start with /) or if we want to check both
  if (typeof parentIdentifier === 'string' && !parentIdentifier.startsWith('/')) {
    parent = modules.find(m => m._id === parentIdentifier);
  } else {
    // Find the parent module by URL
    // We prefer the one that is a Page (no parentModuleId) to avoid getting the first Tab if they share URLs
    parent = modules.find(m => 
      m.moduleUrl?.toLowerCase() === parentIdentifier.toLowerCase() && !m.parentModuleId
    ) || modules.find(m => m.moduleUrl?.toLowerCase() === parentIdentifier.toLowerCase());
  }

  if (!parent) return [];

  // Filter children of this parent.
  return modules
    .filter(m => m.parentModuleId === parent._id)
    .sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0));
};
